import React, { useState, useEffect } from 'react';
import { X, ChevronRight } from 'lucide-react';
import { usePublicTranslation } from '../../../hooks/usePublicTranslation';
import { setAnalyticsConsent } from '../../../services/analytics';

interface CookiePreferences {
    necessary: boolean;
    functional: boolean;
    analytics: boolean;
    performance: boolean;
    advertisement: boolean;
}

const CookieConsentBanner: React.FC = () => {
    const { t } = usePublicTranslation();
    const [isVisible, setIsVisible] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
    const [preferences, setPreferences] = useState<CookiePreferences>({
        necessary: true,
        functional: false,
        analytics: false,
        performance: false,
        advertisement: false,
    });

    const cookieConsent = t.cookieConsent;

    useEffect(() => {
        const consentGiven = localStorage.getItem('cookie_consent_given');
        if (!consentGiven) {
            const timer = setTimeout(() => setIsVisible(true), 800);
            return () => clearTimeout(timer);
        }
    }, []);

    const saveConsent = (prefs: CookiePreferences) => {
        localStorage.setItem('cookie_consent_given', 'true');
        localStorage.setItem('cookie_preferences', JSON.stringify(prefs));
        setAnalyticsConsent(prefs.analytics || prefs.performance);
        setIsVisible(false);
        setShowModal(false);
    };

    const handleAcceptAll = () => {
        saveConsent({ necessary: true, functional: true, analytics: true, performance: true, advertisement: true });
    };

    const handleRejectAll = () => {
        saveConsent({ necessary: true, functional: false, analytics: false, performance: false, advertisement: false });
    };

    const handleSavePreferences = () => {
        saveConsent(preferences);
    };

    const togglePreference = (key: keyof CookiePreferences) => {
        if (key === 'necessary') return;
        setPreferences(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const toggleCategory = (category: string) => {
        setExpandedCategory(expandedCategory === category ? null : category);
    };

    if (!isVisible) return null;

    const categories = [
        { key: 'necessary', isAlwaysActive: true },
        { key: 'functional', isAlwaysActive: false },
        { key: 'analytics', isAlwaysActive: false },
        { key: 'performance', isAlwaysActive: false },
        { key: 'advertisement', isAlwaysActive: false },
    ] as const;

    return (
        <>
            {/* Backdrop blur for main banner */}
            {!showModal && (
                <div className="fixed inset-0 bg-black/20 backdrop-blur-[2px] z-[9998]" />
            )}

            {/* Main Banner */}
            {!showModal && (
                <div className="fixed bottom-0 left-0 right-0 z-[9999] animate-slide-up">
                    <div className="bg-white/95 backdrop-blur-md shadow-[0_-4px_20px_rgba(0,0,0,0.15)] border-t border-gray-200">
                        <div className="max-w-4xl mx-auto px-4 py-6 md:px-8">
                            <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-8">
                                {/* Text */}
                                <div className="flex-1">
                                    <h3 className="text-lg font-bold text-dark mb-2">
                                        {cookieConsent.title}
                                    </h3>
                                    <p className="text-sm text-gray-600 leading-relaxed">
                                        {cookieConsent.description}
                                    </p>
                                </div>

                                {/* Buttons */}
                                <div className="flex flex-col sm:flex-row gap-3 sm:flex-shrink-0">
                                    <button
                                        onClick={() => setShowModal(true)}
                                        className="px-6 py-3 text-sm font-medium text-primary border-2 border-primary rounded-lg hover:bg-primary/5 transition-colors"
                                    >
                                        {cookieConsent.customize}
                                    </button>
                                    <button
                                        onClick={handleRejectAll}
                                        className="px-6 py-3 text-sm font-medium text-gray-700 border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                    >
                                        {cookieConsent.rejectAll}
                                    </button>
                                    <button
                                        onClick={handleAcceptAll}
                                        className="px-6 py-3 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 transition-colors"
                                    >
                                        {cookieConsent.acceptAll}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Preferences Modal */}
            {showModal && (
                <>
                    {/* Backdrop */}
                    <div
                        className="fixed inset-0 bg-black/50 z-[9998]"
                        onClick={() => setShowModal(false)}
                    />

                    {/* Modal */}
                    <div className="fixed inset-4 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-2xl md:max-h-[85vh] bg-white/95 backdrop-blur-md rounded-xl shadow-2xl z-[9999] flex flex-col animate-fade-in">
                        {/* Header */}
                        <div className="flex items-center justify-between p-6 border-b border-gray-200">
                            <h2 className="text-xl font-bold text-dark">
                                {cookieConsent.modalTitle}
                            </h2>
                            <button
                                onClick={() => setShowModal(false)}
                                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Content */}
                        <div className="flex-1 overflow-y-auto p-6">
                            <p className="text-sm text-gray-600 mb-6 leading-relaxed whitespace-pre-line">
                                {cookieConsent.modalDescription}
                            </p>

                            {/* Cookie Categories */}
                            <div className="space-y-3">
                                {categories.map(({ key, isAlwaysActive }) => (
                                    <div key={key} className="border border-gray-200 rounded-lg overflow-hidden">
                                        <button
                                            onClick={() => toggleCategory(key)}
                                            className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                                        >
                                            <div className="flex items-center gap-3">
                                                <ChevronRight
                                                    className={`w-5 h-5 text-gray-400 transition-transform ${
                                                        expandedCategory === key ? 'rotate-90' : ''
                                                    }`}
                                                />
                                                <span className="font-semibold text-dark">
                                                    {cookieConsent.categories[key].title}
                                                </span>
                                            </div>
                                            {isAlwaysActive ? (
                                                <span className="text-sm font-medium text-green-600">
                                                    {cookieConsent.alwaysActive}
                                                </span>
                                            ) : (
                                                <label
                                                    className="relative inline-flex items-center cursor-pointer"
                                                    onClick={e => e.stopPropagation()}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={preferences[key]}
                                                        onChange={() => togglePreference(key)}
                                                        className="sr-only peer"
                                                    />
                                                    <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                                                </label>
                                            )}
                                        </button>
                                        {expandedCategory === key && (
                                            <div className="px-12 pb-4 text-sm text-gray-600 leading-relaxed">
                                                {cookieConsent.categories[key].description}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="p-6 border-t border-gray-200 bg-gray-50">
                            <div className="flex flex-col sm:flex-row gap-3">
                                <button
                                    onClick={handleRejectAll}
                                    className="flex-1 px-6 py-3 text-sm font-medium text-primary border-2 border-primary rounded-lg hover:bg-primary/5 transition-colors"
                                >
                                    {cookieConsent.rejectAll}
                                </button>
                                <button
                                    onClick={handleSavePreferences}
                                    className="flex-1 px-6 py-3 text-sm font-medium text-primary border-2 border-primary rounded-lg hover:bg-primary/5 transition-colors"
                                >
                                    {cookieConsent.savePreferences}
                                </button>
                                <button
                                    onClick={handleAcceptAll}
                                    className="flex-1 px-6 py-3 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 transition-colors"
                                >
                                    {cookieConsent.acceptAll}
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}

            <style>{`
                @keyframes slide-up {
                    from { transform: translateY(100%); opacity: 0; }
                    to { transform: translateY(0); opacity: 1; }
                }
                @keyframes fade-in {
                    from { opacity: 0; transform: scale(0.95); }
                    to { opacity: 1; transform: scale(1); }
                }
                .animate-slide-up { animation: slide-up 0.3s ease-out; }
                .animate-fade-in { animation: fade-in 0.2s ease-out; }
            `}</style>
        </>
    );
};

export default CookieConsentBanner;
